import { FC, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Button, IconButton } from '@shared/components';
import { FormInput } from '@components/FormInput/ui/FormInput.tsx';
import ArrowIcon from '@icons/ArrowIcon/ArrowIcon.tsx';
import { resendVerificationCode, verifyUser } from '@api/auth/apiService.ts';
import { VerifyUserResponse } from '@api/auth/model/types.ts';
import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast.tsx';
import { useNavigate } from 'react-router-dom';
import { SignUpFormDefaultValues } from '@providers/SignUpFormProvider/model/types';
import { useSignUpFormContext } from '@providers/SignUpFormProvider/model/SignUpFormContext';
import { object } from 'yup';
import * as yup from 'yup';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import { routes } from '@shared/constants/paths.ts';
import apiHandler from '@api/ApiHandler';
import { CaseSubscriptionApi } from '@api/case-subscription/caseSubscription';
import Grid from '@mui/material/Grid';
import { Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import styles from './SignUpVerifyUserForm.module.scss';

interface ISignUpVerifyUserFormProps {}

interface SignUpVerifyUserForm {
  verifyCode: string;
}

const SignUpVerifyUserForm: FC<ISignUpVerifyUserFormProps> = () => {
  const login = useAuthSelector((state) => state.login);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { signUpFormState } = useSignUpFormContext();
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    if (!signUpFormState.email) {
      // Sign-in, not sign-up. Anyone landing here without an email in state has an
      // account already, so sign-up would only reject them for a duplicate address.
      // Signing in is the actual route back in: it returns 403 and forwards here
      // again, this time with the email set. See ZLA-002.
      navigate(routes.signIn);
    }
  }, []);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignUpVerifyUserForm>({
    mode: 'onChange',
    defaultValues: {
      verifyCode: SignUpFormDefaultValues.verifyCode,
    },
    resolver: yupResolver(
      object().shape({
        verifyCode: yup.string().required(),
      })
    ),
  });

  const onSubmit = (data: SignUpVerifyUserForm) => {
    const verifyUserRequestData = {
      email: signUpFormState.email,
      code: data.verifyCode,
    };

    setIsSubmitting(true);
    verifyUser(
      verifyUserRequestData,
      async (data: VerifyUserResponse) => {
        setIsSubmitting(false);
        toast(({ id }) => (
          <Toast
            id={id}
            type="success"
            title="Success"
            text="Your account has been successfully verified. Welcome to ZlaytAI."
          />
        ));
        login(data);

        // People reach this screen by signing in to an account that was created
        // but never verified, which almost always means they abandoned signup
        // before choosing a plan. Send them to plan selection so they can finish,
        // rather than into an app they cannot use yet. Anyone who already has a
        // subscription goes home as before. See ZLA-002.
        try {
          apiHandler.setAuthToken(data.access);
          const subscription =
            await CaseSubscriptionApi.getCurrentSubscription();
          navigate(
            subscription ? routes.home : routes.settings.subscription.base
          );
        } catch {
          // No subscription (404) or the check failed. Plan selection is the
          // safer landing spot either way, since they can always navigate on.
          navigate(routes.settings.subscription.base);
        }
      },
      (error: any) => {
        setIsSubmitting(false);
        // Check for business approval message (check message content instead of error code)
        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.error;
        const isBusinessNotApproved =
          errorMessage &&
          (errorMessage.includes('pending approval') ||
            errorMessage.includes('BUSINESS_USER_NOT_APPROVED'));

        if (isBusinessNotApproved) {
          toast(({ id }) => (
            <Toast
              id={id}
              type="success"
              title="Success"
              text={
                'Your account has been successfully verified, ' +
                'however it still needs to be approved by a ZlaytAI admin.'
              }
            />
          ));
          navigate(routes.signIn);
        } else {
          // Show it on the code field rather than only as a toast, so the user stays
          // on this screen and can see exactly what to correct.
          setError('verifyCode', {
            type: 'server',
            message: errorMessage || 'An error occurred. Please try again.',
          });
        }
      }
    );
  };

  const handleResend = () => {
    setIsLoading(true);
    resendVerificationCode(
      signUpFormState.email,
      () => {
        setIsLoading(false);
        toast(({ id }) => (
          <Toast
            id={id}
            type="success"
            title="We've resent the code to your email"
            text="Please check your email."
          />
        ));
      },
      (error: any) => {
        setIsLoading(false);
        toast(({ id }) => (
          <Toast
            id={id}
            type="error"
            title="Error"
            text={error?.response?.data?.message}
          />
        ));
      }
    );
  };

  return (
    <Grid container spacing={2} alignItems="center" justifyContent="center">
      <Grid item xs={12}>
        <Grid
          container
          direction={{ xs: 'column-reverse', md: 'row' }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item>
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              paddingX={3}
            >
              <Typography
                color="gray.main"
                gutterBottom
                variant={downMD ? 'h3' : 'h2'}
                textAlign="center"
              >
                Verify your account
              </Typography>
              <Typography
                color="gray"
                gutterBottom
                fontSize={14}
                variant="subtitle2"
                textAlign="center"
              >
                We have sent a verification code to your email.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid item xs={12}>
            <FormInput
              bordered
              type="input"
              labelText="Verification code"
              placeholder="XXXXXX"
              errorText={
                // Show the real reason. This rendered one fixed "required field"
                // string for every error, so a wrong code reported the wrong thing.
                errors?.verifyCode?.message ||
                (errors?.verifyCode
                  ? 'Verification code is a required field'
                  : undefined)
              }
              {...register('verifyCode')}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="end">
              <Typography variant="body2" color="gray">
                Did not receive the verification code?
              </Typography>
              <Button
                variant="underline"
                type="button"
                onClick={handleResend}
                isLoading={isLoading}
              >
                Resend
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2} flexGrow={1} mt={2}>
              <IconButton
                type="button"
                onClick={() => navigate(-1)}
                className={styles.backBtn}
              >
                <ArrowIcon stroke="#667085" />
              </IconButton>
              <Button
                type="submit"
                variant="active"
                className={styles.nextBtn}
                isLoading={isSubmitting}
              >
                Continue
              </Button>
            </Box>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default SignUpVerifyUserForm;
